import {
  AgreeToTermsPolicy,
  experience,
  ExtraParamsKey,
  SignInIdentifier,
  SignInMode,
} from '@logto/schemas';
import { useCallback, useContext, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';

import LandingPageLayout from '@/Layout/LandingPageLayout';
import SingleSignOnFormModeContextProvider from '@/Providers/SingleSignOnFormModeContextProvider';
import SingleSignOnFormModeContext from '@/Providers/SingleSignOnFormModeContextProvider/SingleSignOnFormModeContext';
import ContinueWithPhoneButton from '@/components/Button/ContinueWithPhoneButton';
import Divider from '@/components/Divider';
import GoogleOneTap from '@/components/GoogleOneTap';
import IdentifierRegisterForm from '@/components/IdentifierRegisterForm';
import TextLink from '@/components/TextLink';
import SocialSignInList from '@/containers/SocialSignInList';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import TermsAndPrivacyLinks from '@/containers/TermsAndPrivacyLinks';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import usePlatform from '@/hooks/use-platform';
import { useSieMethods } from '@/hooks/use-sie';
import useTerms from '@/hooks/use-terms';

import ErrorPage from '../ErrorPage';

/** "Continue with phone" below the form, mirroring the sign-in page. */
const RegisterAlternates = () => {
  const { signUpMethods, socialConnectors } = useSieMethods();
  const { showSingleSignOnForm } = useContext(SingleSignOnFormModeContext);

  const hasPhone = signUpMethods.includes(SignInIdentifier.Phone);
  const hasNonPhoneInline = signUpMethods.some(
    (identifier) => identifier !== SignInIdentifier.Phone
  );

  if (showSingleSignOnForm || !(hasPhone && hasNonPhoneInline)) {
    return null;
  }

  // With social leading the page the "or" divider already sits above the form.
  const isSocialPrimary = socialConnectors.length > 0 && signUpMethods.length > 0;

  return isSocialPrimary ? (
    <ContinueWithPhoneButton mode="register" className="mt-3" />
  ) : (
    <>
      <Divider label="description.or" className="my-6" />
      <ContinueWithPhoneButton mode="register" />
    </>
  );
};

const RegisterFooter = () => {
  const { t } = useTranslation();
  const { socialConnectors, signInMode, signInMethods, singleSignOnEnabled } = useSieMethods();
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const navigate = useNavigateWithPreservedSearchParams();
  const [params] = useSearchParams();

  const { showSingleSignOnForm } = useContext(SingleSignOnFormModeContext);

  const handleSsoNavigation = useCallback(async () => {
    if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual && !(await termsValidation())) {
      return;
    }

    navigate('/single-sign-on/email');
  }, [agreeToTermsPolicy, navigate, termsValidation]);

  if (params.get(ExtraParamsKey.OneTimeToken)) {
    return (
      <Navigate
        replace
        to={{ pathname: `/${experience.routes.oneTimeToken}`, search: `?${params.toString()}` }}
      />
    );
  }

  const showSignIn = signInMode === SignInMode.SignInAndRegister && signInMethods.length > 0;

  if (showSingleSignOnForm || (!singleSignOnEnabled && !showSignIn)) {
    return null;
  }

  return (
    <div className="mt-6 flex flex-col gap-3 text-center text-sm text-muted first:mt-0">
      {singleSignOnEnabled && (
        <>
          <div className="flex flex-wrap items-center justify-center gap-x-1 gap-y-1">
            <span>
              <Trans components={{ a: <TextLink onClick={handleSsoNavigation} /> }}>
                {t('description.use_single_sign_on')}
              </Trans>
            </span>
          </div>
          {
            // SSO is the only method, so the agreement has to be collected here.
            signInMethods.length === 0 &&
              socialConnectors.length === 0 &&
              agreeToTermsPolicy === AgreeToTermsPolicy.Manual && (
                <TermsAndPrivacyCheckbox className="justify-center text-start" />
              )
          }
        </>
      )}
      {showSignIn && (
        <div>
          {t('description.have_account')} <TextLink replace to="/sign-in" text="action.sign_in" />
        </div>
      )}
    </div>
  );
};

/** Social providers above the "or" divider and above the form, mirroring the sign-in page. */
const SocialSignUpPrimary = () => {
  const { signUpMethods, socialConnectors } = useSieMethods();
  const { showSingleSignOnForm } = useContext(SingleSignOnFormModeContext);

  // With no sign-up methods the page body renders this same list on its own.
  if (showSingleSignOnForm || socialConnectors.length === 0 || signUpMethods.length === 0) {
    return null;
  }

  return (
    <>
      <SocialSignInList mode="register" socialConnectors={socialConnectors} />
      <Divider label="description.or" className="my-6" />
    </>
  );
};

const Register = () => {
  const { signUpMethods, socialConnectors, signInMode } = useSieMethods();
  const { agreeToTermsPolicy } = useTerms();
  const { isMobile } = usePlatform();

  // Phone gets its own "Continue with phone" button, so it leaves the inline field unless
  // it is the only method.
  const inlineSignUpMethods = useMemo(() => {
    const nonPhone = signUpMethods.filter((identifier) => identifier !== SignInIdentifier.Phone);
    return nonPhone.length > 0 ? nonPhone : signUpMethods;
  }, [signUpMethods]);

  if (!signInMode) {
    return <ErrorPage />;
  }

  if (signInMode === SignInMode.SignIn) {
    return <Navigate to="/sign-in" />;
  }

  return (
    <LandingPageLayout
      title="description.create_your_account"
      description="description.auth_subtitle"
    >
      <GoogleOneTap context="signup" />
      <SingleSignOnFormModeContextProvider>
        <div className="flex w-full flex-col">
          <SocialSignUpPrimary />
          {inlineSignUpMethods.length > 0 && (
            // Autofocusing on mobile would force the keyboard open over the layout.
            <IdentifierRegisterForm autoFocus={!isMobile} signUpMethods={inlineSignUpMethods} />
          )}
          {signUpMethods.length === 0 && socialConnectors.length > 0 && (
            <div className="flex flex-col gap-4">
              {agreeToTermsPolicy !== AgreeToTermsPolicy.Automatic && <TermsAndPrivacyCheckbox />}
              <SocialSignInList mode="register" socialConnectors={socialConnectors} />
            </div>
          )}
          <RegisterAlternates />
          <RegisterFooter />
          {agreeToTermsPolicy === AgreeToTermsPolicy.Automatic && (
            <TermsAndPrivacyLinks className="mt-6 text-center text-xs text-muted first:mt-0" />
          )}
        </div>
      </SingleSignOnFormModeContextProvider>
    </LandingPageLayout>
  );
};

export default Register;
